import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Loader2, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPackages = () => {
  const { language, t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    price: 0,
    currency: 'SAR',
    messages_limit: 1000,
    features_ar: [],
    features_en: [],
    is_popular: false,
    is_active: true,
  });
  const [featuresArText, setFeaturesArText] = useState('');
  const [featuresEnText, setFeaturesEnText] = useState('');

  const token = localStorage.getItem('admin_token');

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/admin/packages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error(language === 'ar' ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataToSend = {
      ...formData,
      features_ar: featuresArText.split('\n').filter(f => f.trim()),
      features_en: featuresEnText.split('\n').filter(f => f.trim()),
    };

    try {
      if (editingPackage) {
        await axios.put(`${API}/admin/packages/${editingPackage.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(language === 'ar' ? 'تم تحديث الباقة بنجاح' : 'Package updated successfully');
      } else {
        await axios.post(`${API}/admin/packages`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(language === 'ar' ? 'تم إضافة الباقة بنجاح' : 'Package added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error(language === 'ar' ? 'خطأ في حفظ البيانات' : 'Error saving data');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) return;
    
    try {
      await axios.delete(`${API}/admin/packages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error(language === 'ar' ? 'خطأ في الحذف' : 'Error deleting');
    }
  };

  const openEditDialog = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name_ar: pkg.name_ar,
      name_en: pkg.name_en,
      description_ar: pkg.description_ar,
      description_en: pkg.description_en,
      price: pkg.price,
      currency: pkg.currency,
      messages_limit: pkg.messages_limit,
      features_ar: pkg.features_ar,
      features_en: pkg.features_en,
      is_popular: pkg.is_popular,
      is_active: pkg.is_active,
    });
    setFeaturesArText(pkg.features_ar.join('\n'));
    setFeaturesEnText(pkg.features_en.join('\n'));
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPackage(null);
    setFormData({
      name_ar: '',
      name_en: '',
      description_ar: '',
      description_en: '',
      price: 0,
      currency: 'SAR',
      messages_limit: 1000,
      features_ar: [],
      features_en: [],
      is_popular: false,
      is_active: true,
    });
    setFeaturesArText('');
    setFeaturesEnText('');
  };

  return (
    <div className="space-y-6" data-testid="admin-packages">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('admin_packages')}</h1>
          <p className="text-slate-500">
            {language === 'ar' ? 'إدارة باقات الخدمة' : 'Manage service packages'}
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-package-btn"
        >
          <Plus className="w-4 h-4" />
          {t('admin_add_package')}
        </Button>
      </div>

      {/* Packages List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : packages.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500">
              {language === 'ar' ? 'لا توجد باقات بعد' : 'No packages yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`border-none shadow-sm relative ${!pkg.is_active && 'opacity-60'} ${pkg.is_popular && 'ring-2 ring-emerald-500'}`} data-testid={`package-item-${index}`}>
                {pkg.is_popular && (
                  <div className="absolute -top-3 start-4 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-medium flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    {t('packages_popular')}
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-slate-900 text-lg">
                        {language === 'ar' ? pkg.name_ar : pkg.name_en}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {language === 'ar' ? pkg.description_ar : pkg.description_en}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(pkg)}
                        className="h-8 w-8"
                        data-testid={`edit-package-${index}`}
                      >
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(pkg.id)}
                        className="h-8 w-8"
                        data-testid={`delete-package-${index}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-slate-900">{pkg.price}</span>
                    <span className="text-slate-500 ms-1">{pkg.currency}</span>
                    <span className="text-slate-400 text-sm">{t('packages_month')}</span>
                  </div>

                  <div className="text-sm text-emerald-600 font-medium mb-3">
                    {pkg.messages_limit.toLocaleString()} {t('packages_messages')}
                  </div>

                  <ul className="space-y-1 text-sm text-slate-600">
                    {(language === 'ar' ? pkg.features_ar : pkg.features_en).slice(0, 3).map((feature, i) => (
                      <li key={i}>• {feature}</li>
                    ))}
                    {(language === 'ar' ? pkg.features_ar : pkg.features_en).length > 3 && (
                      <li className="text-slate-400">
                        +{(language === 'ar' ? pkg.features_ar : pkg.features_en).length - 3} {language === 'ar' ? 'ميزات أخرى' : 'more features'}
                      </li>
                    )}
                  </ul>

                  <div className="mt-4 flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${pkg.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {pkg.is_active ? (language === 'ar' ? 'نشط' : 'Active') : (language === 'ar' ? 'غير نشط' : 'Inactive')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="package-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? t('admin_edit') : t('admin_add_package')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_name_ar')}
                </label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  required
                  data-testid="package-name-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_name_en')}
                </label>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  required
                  data-testid="package-name-en"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_desc_ar')}
                </label>
                <Textarea
                  value={formData.description_ar}
                  onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                  required
                  data-testid="package-desc-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_desc_en')}
                </label>
                <Textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  required
                  data-testid="package-desc-en"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_price')}
                </label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  required
                  data-testid="package-price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {language === 'ar' ? 'العملة' : 'Currency'}
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full h-10 rounded-lg border border-slate-200 px-3"
                  data-testid="package-currency"
                >
                  <option value="SAR">SAR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_messages_limit')}
                </label>
                <Input
                  type="number"
                  value={formData.messages_limit}
                  onChange={(e) => setFormData({ ...formData, messages_limit: parseInt(e.target.value) })}
                  required
                  data-testid="package-messages"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_features_ar')} ({language === 'ar' ? 'كل ميزة بسطر' : 'one per line'})
                </label>
                <Textarea
                  value={featuresArText}
                  onChange={(e) => setFeaturesArText(e.target.value)}
                  rows={4}
                  placeholder={language === 'ar' ? '1000 رسالة شهرياً\nربط حساب واحد\nدعم فني' : '1000 messages/month\n1 account\nSupport'}
                  data-testid="package-features-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_features_en')} ({language === 'ar' ? 'كل ميزة بسطر' : 'one per line'})
                </label>
                <Textarea
                  value={featuresEnText}
                  onChange={(e) => setFeaturesEnText(e.target.value)}
                  rows={4}
                  placeholder="1000 messages/month\n1 account connection\nEmail support"
                  data-testid="package-features-en"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-slate-700">{t('admin_popular')}</label>
              <Switch
                checked={formData.is_popular}
                onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
                data-testid="package-popular"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-slate-700">{t('admin_active')}</label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="package-active"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('admin_cancel')}
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-package-btn">
                {t('admin_save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPackages;
